import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MapPin, Users } from "lucide-react";
import { useQueryClient } from "react-query";
import { useAuth } from "../../providers/auth";
import {
  normalizeEmail,
  useCommunityHubById,
  useHubMembers,
  useHubModerators,
  useHubPosts,
  userIsHubAmbassador,
} from "../../data/communityHub";
import CommunityHubLogo from "../../components/community/shared/CommunityHubLogo";
import AmbassadorBanner from "../../components/community/seeker/AmbassadorBanner";
import CommunityPostComposer from "../../components/community/feed/CommunityPostComposer";
import CommunityFeedPost from "../../components/community/feed/CommunityFeedPost";
import ManageMembersModal from "../../components/community/admin/ManageMembersModal";
import ImageLightbox from "../../components/EventManagement/Component/ImageLightBox";
import ToastAlert from "../../components/custom-ui/toast-alert/ToastAlert";
import useModal from "../../hooks/useModal";

export default function SeekerCommunityDetailsPage() {
  const { communityId } = useParams<{ communityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user }: any = useAuth();
  const qc = useQueryClient();
  const hubId = communityId || "";

  const { data: hubFromDb, isLoading } = useCommunityHubById(hubId);
  const community = hubFromDb || location.state?.community;
  const { data: extraModerators = [] } = useHubModerators(hubId);
  const { data: members = [] } = useHubMembers(hubId);
  const { data: posts = [] } = useHubPosts(hubId);
  const membersModal = useModal();

  const isAmbassador = userIsHubAmbassador(
    community,
    user?.email,
    extraModerators.map((m: any) => m.email),
  );

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxData, setLightboxData] = useState(null as {
    images: string[];
    title: string;
    description: string;
    author: string;
    post_type: string;
  } | null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openPostLightbox = (
    post: { title?: string; body?: string; author?: { name?: string } },
    images: string[],
    index: number,
  ) => {
    setLightboxData({
      images,
      title: post.title || "",
      description: post.body || "",
      author: post.author?.name || "",
      post_type: "post",
    });
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (!community) {
    if (isLoading) return null;
    return (
      <div className="pt-44 px-6 text-center text-gray-500">
        Community not found.
        <button
          type="button"
          className="block mx-auto mt-4 text-[#1E596D] font-semibold"
          onClick={() => navigate("/community")}
        >
          ← Back to Communities
        </button>
      </div>
    );
  }

  const memberEmails = (members || []).map((m: any) => normalizeEmail(m.email));
  const moderatorEmails = [
    normalizeEmail(community.moderatorBy?.email),
    ...(extraModerators || []).map((m: any) => normalizeEmail(m.email)),
  ].filter(Boolean);

  return (
    <div className="pt-[180px] overflow-y-auto h-screen md:pt-44 min-h-screen bg-[#F4F7F8]">
      <div className="max-w-[95%] md:max-w-[90%] mx-auto px-4 md:px-6 mb-3 md:mb-4">
        <nav className="flex items-center gap-2 text-xs md:text-sm">
          <button
            type="button"
            onClick={() => navigate("/community")}
            className="text-gray-500 hover:text-[#15415E] font-medium transition-colors flex items-center gap-1"
          >
            &larr; Communities
          </button>
          <span className="text-gray-400">/</span>
          <span className="text-[#15415E] font-semibold truncate">
            {community.hubName}
          </span>
        </nav>
      </div>

      {isAmbassador && (
        <div className="max-w-[95%] md:max-w-[90%] mx-auto px-4 md:px-6">
          <AmbassadorBanner hubName={community.hubName} />
        </div>
      )}

      <div className="max-w-[95%] md:max-w-[90%] mx-auto px-4 md:px-6 mb-4 md:mb-6">
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex items-start gap-3 md:gap-4 min-w-0">
            <CommunityHubLogo
              hubName={community.hubName}
              size={44}
              className="md:hidden"
            />
            <CommunityHubLogo
              hubName={community.hubName}
              size={56}
              className="hidden md:grid"
            />
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-extrabold text-[#15415E] leading-tight">
                {community.hubName}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs md:text-sm text-gray-500">
                <span className="flex items-center gap-1 font-medium">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="text-gray-400"
                  >
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <b className="text-[#15415E]">
                    {(community.hubMembers ?? community.memberCount ?? 0).toLocaleString()}
                  </b>{" "}
                  members
                </span>
                {community.moderatorBy?.name && (
                  <>
                    <span className="text-gray-300">•</span>
                    <span>
                      Moderated by{" "}
                      <b className="text-[#15415E]">
                        {community.moderatorBy.name}
                      </b>
                    </span>
                  </>
                )}
                {community.location?.trim() ? (
                  <>
                    <span className="text-gray-300">•</span>
                    <span className="flex items-center gap-1">
                      <MapPin size={13} className="text-gray-400" />
                      {community.location.trim()}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {isAmbassador && (
            <button
              type="button"
              onClick={membersModal.toggle}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-[#15415E] bg-white border border-[#15415E] rounded-full hover:bg-[#F4F7F8] transition-colors shrink-0"
            >
              <Users size={16} />
              Manage members
            </button>
          )}
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-4 md:px-6 pb-10 md:pb-20 space-y-4 md:space-y-5">
        {isAmbassador && <CommunityPostComposer hubId={community.id} />}

        {posts.map((post: any) => (
          <CommunityFeedPost
            key={post.id}
            hubId={community.id}
            post={post}
            canModerate={isAmbassador}
            canComment
            commentEditWindowMs={isAmbassador ? 60_000 : 300_000}
            onOpenLightbox={(images: string[], index: number) =>
              openPostLightbox(post, images, index)
            }
          />
        ))}

        {posts.length === 0 && (
          <p className="text-center text-gray-400 py-12 bg-white rounded-2xl border border-gray-100 shadow-sm">
            {isAmbassador
              ? "No posts yet. Create the first post above."
              : "No posts yet."}
          </p>
        )}
      </div>

      {membersModal.isShowing && (
        <ManageMembersModal
          hubId={community.id}
          members={members}
          excludeEmails={[...memberEmails, ...moderatorEmails]}
          onClose={membersModal.toggle}
          onSuccess={() => {
            qc.invalidateQueries(["hubMembers", community.id]);
            qc.invalidateQueries(["communityHub", community.id]);
          }}
        />
      )}

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        data={lightboxData}
        initialIndex={lightboxIndex}
        isfromcommunity
      />

      <ToastAlert containerStyle={{ zIndex: 10000 }} />
    </div>
  );
}
